import { Translate } from '@components'

import {
  InternalAccessor,
  ModalConfirmation,
  NavItem,
  notify,
  Table,
  TableColumn,
  TableHoverActions,
  TableStaticActions,
  useModal,
} from '@billy/design-system'
import { Fragment, ReactElement, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../../../../../enums/notificationKeys'
import { TableIds } from '../../../../../../../../../../enums/tableIds'
import { APIError } from '../../../../../../../../../../utils'
import { deleteOrganizationOwner } from '../../../../../../../../../settings/routes/OrganizationSettings/query-api'
import { OrganizationOwner } from '../../../../../../../../../settings/routes/OrganizationSettings/types/organizationOwner'
import { useOrganizationOwners } from '../../../../../../contexts/organizationOwnersContext'
import { useFetchOrganizationOwnersData } from '../../../../../../hooks/useFetchOrganizationOwnersData'
import { useOrganizationOwnersTableData } from '../../../../../../hooks/useOrganizationOwnersTableData'
import { OrganizationOwnerStaticActionType } from '../../../../enums/organizationOwnersStaticActionType'
import { OrganizationOwnersTableRow } from '../../../../types/organizationOwnersTableRow'

interface OwnersTableProps {
  isProprietorship?: boolean
}

export const OrganizationOwnersTable = ({ isProprietorship }: OwnersTableProps): ReactElement => {
  const { t } = useTranslation()
  const { openEditOwnerModal } = useOrganizationOwners()
  const { isLoading, organizationOwnersData, refetchOrganizationOwnersData } = useFetchOrganizationOwnersData()
  const tableData = useOrganizationOwnersTableData(organizationOwnersData)

  const { open: openDeleteOrganizationOwnerModal, close: closeDeleteOrganizationOwnerModal } = useModal(
    ModalId.DeleteOrganizationOwnerModal,
  )

  const handleEditOwnerButtonClick = useCallback(
    (organizationOwnerId: string) => {
      openEditOwnerModal?.(organizationOwnerId)
    },
    [openEditOwnerModal],
  )

  const handleDeleteOwnerButtonClick = useCallback(
    (owner: OrganizationOwner) => {
      targetedOrganizationOwner.current = owner
      openDeleteOrganizationOwnerModal()
    },
    [openDeleteOrganizationOwnerModal],
  )

  /** I temporarily keep the whole object here, because we need more than just `id` */
  const targetedOrganizationOwner = useRef<OrganizationOwner | null>(null)

  const { mutate } = useMutation(deleteOrganizationOwner, {
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.OrganizationOwnerDelete,
        message: error?.message,
        variant: 'error',
      })
    },
    onSuccess: () => {
      targetedOrganizationOwner.current = null
      closeDeleteOrganizationOwnerModal()
      refetchOrganizationOwnersData()
      notify({
        id: NotificationKeys.OrganizationOwnerDelete,
        message: t('organization_owner_table.confirmed_deletion.organization_owner'),
        variant: 'success',
      })
    },
  })

  const staticActionsItems: NavItem<OrganizationOwnerStaticActionType>[] = useMemo(
    () => [
      {
        children: t('settings.organization.organization_info.owners_table.actions.delete'),
        iconLeft: 'trash',
        id: 'delete',
        value: OrganizationOwnerStaticActionType.Delete,
      },
    ],
    [t],
  )

  const proprietorsColumns: TableColumn<OrganizationOwnersTableRow>[] = useMemo(
    () => [
      {
        accessor: 'name',
        Header: <Translate value="settings.organization.organization_info.owners_table.header.name" />,
        size: 's',
      },
      {
        accessor: 'address',
        Header: <Translate value="settings.organization.organization_info.owners_table.header.address" />,
      },
      {
        accessor: InternalAccessor.HoverActions,
        Cell: ({ row, data }) => (
          <TableHoverActions>
            <TableHoverActions.Item
              icon="squarePen"
              label={t('settings.organization.organization_info.owners_table.actions.edit')}
              onClick={() => {
                const { id } = data[row.id] as OrganizationOwnersTableRow
                handleEditOwnerButtonClick(id)
              }}
            />
          </TableHoverActions>
        ),
        Header: '',
      },
      {
        accessor: InternalAccessor.StaticActions,
        Cell: ({ row, data }) => (
          <TableStaticActions
            items={staticActionsItems}
            onSelect={() => {
              const owner = data[row.id] as OrganizationOwner
              handleDeleteOwnerButtonClick(owner)
            }}
          />
        ),
        Header: '',
      },
    ],
    [handleDeleteOwnerButtonClick, handleEditOwnerButtonClick, staticActionsItems, t],
  )

  const ownersColumns: TableColumn<OrganizationOwnersTableRow>[] = useMemo(
    () => [
      ...proprietorsColumns,
      {
        accessor: 'nominalOwnershipPercent',
        Header: <Translate value="settings.organization.organization_info.owners_table.header.ownership_share" />,
        size: 's',
      },
    ],
    [proprietorsColumns],
  )

  const onDeleteOwner = useCallback(() => {
    if (targetedOrganizationOwner.current) {
      mutate(targetedOrganizationOwner.current.id)
    }
  }, [mutate])

  return (
    <Fragment>
      <Table
        columns={isProprietorship ? proprietorsColumns : ownersColumns}
        data={tableData}
        id={TableIds.OrganizationOwners}
        isLoading={isLoading}
        skeletonItemsPerPage={2}
        withColumnsFiltering={false}
      />
      {/* Delete owner modal */}
      <ModalConfirmation
        danger
        cancelLabel={t('no')}
        id={ModalId.DeleteOrganizationOwnerModal}
        message={t('organization_owner_table.confirm_deletion', {
          name: targetedOrganizationOwner.current?.name,
        })}
        okLabel={t('yes')}
        onOk={onDeleteOwner}
        title={t('organization_owner_table.confirm_delete')}
      />
    </Fragment>
  )
}
